const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const paginationValue = (response, pagination, key, fallback) =>
  response && response[key] != null ? response[key] : pagination && pagination[key] != null ? pagination[key] : fallback;

export const normalizeStockQuantity = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
  const quantity = Number(value);
  return Number.isInteger(quantity) && quantity >= 0 ? quantity : null;
};

export const normalizeProduct = (value = {}) => {
  const product = value && typeof value === "object" && value.data && !Array.isArray(value.data) ? value.data : value;
  const normalized = { ...(product && typeof product === "object" ? product : {}) };
  if (Object.prototype.hasOwnProperty.call(normalized, "name")) normalized.name = normalized.name != null ? String(normalized.name) : "";
  if (Object.prototype.hasOwnProperty.call(normalized, "sku")) normalized.sku = normalized.sku != null ? String(normalized.sku) : "";
  if (Object.prototype.hasOwnProperty.call(normalized, "stock_quantity")) {
    normalized.stock_quantity = normalizeStockQuantity(normalized.stock_quantity);
  }
  if (normalized.supplier && typeof normalized.supplier === "object") {
    normalized.supplier = { ...normalized.supplier, name: normalized.supplier.name != null ? String(normalized.supplier.name) : "" };
  }
  return normalized;
};

const enabledFlag = (value) => (
  value === true || value === 1 || value === "1" || value === "true" || value === "on"
);

export const normalizeProductFormValues = (value = {}) => {
  const normalized = normalizeProduct(value);
  return {
    ...normalized,
    manage_stock: enabledFlag(normalized.manage_stock) ? 1 : "",
  };
};

export const normalizeProductMutationValues = (values = {}) => ({
  ...values,
  manage_stock: enabledFlag(values.manage_stock) ? 1 : 0,
});

export const normalizeProductDetailResponse = (response) => normalizeProduct(response);

export const normalizeProductListResponse = (response) => {
  const source = response && typeof response === "object" && response.data && !Array.isArray(response.data) && response.data.data
    ? response.data
    : response;
  const pagination = source && (source.pagination || source.meta) && typeof (source.pagination || source.meta) === "object"
    ? source.pagination || source.meta
    : {};
  const isProductRecord = (value) => value && typeof value === "object" && !Array.isArray(value)
    && (value.id != null || value.product_id != null)
    && (value.name != null || value.product_name != null || value.sku != null);
  const rows = Array.isArray(source)
    ? source
    : Array.isArray(source && source.data)
      ? source.data
      : isProductRecord(source && source.data)
        ? [source.data]
        : isProductRecord(source)
          ? [source]
          : asArray(source && source.items);
  const currentPage = Number(paginationValue(source, pagination, "current_page", paginationValue(source, pagination, "page", 1))) || 1;
  const lastPage = Number(paginationValue(source, pagination, "last_page", paginationValue(source, pagination, "pages", currentPage))) || currentPage;
  const nextPageUrl = paginationValue(source, pagination, "next_page_url", currentPage < lastPage ? `afterlogin/products/view?page=${currentPage + 1}` : null);

  return {
    ...(source && !Array.isArray(source) ? source : {}),
    current_page: currentPage,
    data: rows.map(normalizeProduct),
    last_page: lastPage,
    next_page_url: nextPageUrl,
    per_page: Number(paginationValue(source, pagination, "per_page", rows.length)) || rows.length,
    prev_page_url: paginationValue(source, pagination, "prev_page_url", null),
    total: Number(paginationValue(source, pagination, "total", rows.length)) || rows.length,
  };
};

export const hasNextProductPage = (response) => {
  if (!response || typeof response !== "object") return false;
  if (response.next_page_url) return true;
  const current = Number(response.current_page || response.page || 1);
  const last = Number(response.last_page || response.pages || current);
  return current < last;
};

export const normalizeStockAdjustmentReasons = (response) => {
  const source = response && typeof response === "object" && !Array.isArray(response) ? response : {};
  const rows = Array.isArray(source.reasons) ? source.reasons : asArray(source.data);
  const seen = new Set();
  const options = [];
  rows.forEach((row) => {
    const name = row && row.name != null ? String(row.name).trim() : "";
    const key = name.toLowerCase();
    if (!name || seen.has(key)) return;
    seen.add(key);
    options.push({
      id: row && row.id != null ? row.id : null,
      name,
      scope: row && row.scope ? String(row.scope) : "platform",
    });
  });
  return options
    .map((option, index) => ({ option, index }))
    .sort((left, right) => {
      const leftSalon = left.option.scope === "salon" ? 0 : 1;
      const rightSalon = right.option.scope === "salon" ? 0 : 1;
      return leftSalon - rightSalon || left.index - right.index;
    })
    .map(({ option }) => option);
};

export const normalizeStockMovements = (response) => {
  const source = response && typeof response === "object" && !Array.isArray(response) ? response : {};
  const rows = Array.isArray(source.movements) ? source.movements : asArray(source.data);
  return rows.map((row) => ({
    ...(row && typeof row === "object" ? row : {}),
    quantity_change: Number(row && row.quantity_change) || 0,
    resulting_quantity: Number(row && row.resulting_quantity) || 0,
  }));
};

export const applyStockQuantityToList = (list, productId, stockQuantity) => {
  if (!list || !Array.isArray(list.data)) return list || null;
  const quantity = normalizeStockQuantity(stockQuantity);
  if (quantity === null) return list;
  return {
    ...list,
    data: list.data.map((row) =>
      row && String(row.id) === String(productId) ? { ...row, stock_quantity: quantity } : row,
    ),
  };
};

export const applyStockQuantityToProduct = (product, productId, stockQuantity) => {
  const quantity = normalizeStockQuantity(stockQuantity);
  if (!product || String(product.id) !== String(productId) || quantity === null) return product;
  return { ...product, stock_quantity: quantity };
};
